import {DefinitionsList as DefinitionsListShared} from '@github-ui/custom-properties-core/components/DefinitionsList'
import {ListEmptyState} from '@github-ui/custom-properties-core/components/ListEmptyState'
import type {
  BusinessCustomPropertiesDefinitionsPagePayload,
  PropertyDefinition,
} from '@github-ui/custom-properties-types'
import {businessOrgCustomPropertyDetailsPath, customPropertyDetailsPath} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {Pagination, Stack} from '@primer/react'
import {useState} from 'react'

import {getShuffledSuggestions} from '../components/definition-ice-breaker-suggestions'
import {DefinitionIceBreakers} from '../components/DefinitionIceBreakers'
import {DefinitionsPageHeader} from '../components/PropertiesHeaderPageTabs'
import {usePropertiesListResults} from '../hooks/use-properties-list'
import {usePropertySource} from '../hooks/use-property-source'
import {DefinitionsFilter} from './DefinitionsFilter'

const DEFAULT_FILTER_VALUE = 'managed-by:enterprise'
const ICE_BREAKERS_DISPLAY_THRESHOLD = 3

// Shuffled once per JS module evaluation. Computed at module scope so the displayed order is stable across re-renders.
// Note: in SSR, module scope state is shared across requests; DefinitionIceBreakers renders only in the browser to avoid hydration mismatches.
const iceBreakerSuggestions = getShuffledSuggestions()

export function BusinessCustomPropertiesPage() {
  const {ownDefinitionsCount, managedByProviders, ...payload} =
    useRoutePayload<BusinessCustomPropertiesDefinitionsPagePayload>()
  const {sourceName} = usePropertySource()

  const {
    payload: {totalCount, pageCount, definitions, currentQ},
    currentPage,
    fetchResults,
  } = usePropertiesListResults({initialPayload: payload})

  const [filterQuery, setFilterQuery] = useState(currentQ)

  const showIceBreakers = ownDefinitionsCount < ICE_BREAKERS_DISPLAY_THRESHOLD && currentQ === DEFAULT_FILTER_VALUE

  return (
    <>
      <DefinitionsPageHeader permissions="definitions" ownDefinitionsCount={ownDefinitionsCount} />
      <DefinitionsFilter
        className="tmp-mb-3"
        variant="input"
        filterValue={filterQuery}
        onChange={setFilterQuery}
        businessSlug={sourceName}
        onSubmit={request => fetchResults({page: 1, filterQuery: request.raw})}
        managedByProviders={managedByProviders}
      />

      <Stack>
        <DefinitionsListShared
          totalCount={totalCount}
          definitions={definitions}
          renderItem={definition => (
            <DefinitionsListShared.Item
              key={definition.source.slug + definition.propertyName}
              settingsLevel="business"
              definition={definition}
              linkTo={getPropertyDetailsPath(definition, sourceName)}
            />
          )}
          // Ice-breakers replace the "No properties have been found" message when the enterprise has no properties.
          // In this case, screen reader users may not have any visual context to know that no properties exist.
          // So we still use the definitions list component because it announces "0 properties found".
          renderEmptyState={showIceBreakers ? renderNothing : undefined}
        />
        {showIceBreakers && <IceBreakers />}
      </Stack>

      {pageCount > 1 && (
        <Pagination
          currentPage={currentPage}
          pageCount={pageCount}
          onPageChange={(e, page) => {
            e.preventDefault() // Prevent click event from navigating
            fetchResults({page, filterQuery})
          }}
        />
      )}
    </>
  )
}

function IceBreakers() {
  return (
    <ListEmptyState>
      <DefinitionIceBreakers suggestions={iceBreakerSuggestions} />
    </ListEmptyState>
  )
}

function getPropertyDetailsPath(definition: PropertyDefinition, sourceName: string) {
  if (definition.source.type === 'business') {
    return customPropertyDetailsPath({
      pathPrefix: 'enterprises',
      sourceName,
      propertyName: definition.propertyName,
    })
  }

  return businessOrgCustomPropertyDetailsPath({
    business: sourceName,
    org: definition.source.slug,
    propertyName: definition.propertyName,
  })
}

function renderNothing() {
  return null
}
